<template>
  <div>
    <!-- ── Page head ── -->
    <div class="pg-head">
      <div class="pg-ico"><span class="mdi mdi-account-cog"></span></div>
      <div>
        <div class="id-h1">{{ $t('accountMgmt.listTitle') }}</div>
        <div class="pg-sub">{{ $t('accountMgmt.subtitle') }}</div>
      </div>
    </div>

    <div class="scard" style="padding:14px">
      <!-- toolbar: search + usertype filter (head only) + refresh -->
      <div class="grid-toolbar">
        <!-- name/autocomplete กันเบราว์เซอร์เข้าใจผิดว่าเป็นช่อง "ชื่อผู้ใช้" แล้วเติมค่าให้เอง
             (คู่กับ autocomplete="new-password" ในแท็บรหัสผ่านของ dialog ด้านล่าง) -->
        <label class="grid-search"><span class="mdi mdi-magnify"></span>
          <input class="id-input" v-model="search" :placeholder="$t('btn.search')"
            name="user-search" autocomplete="off"></label>
        <id-select v-if="isHeadUser" class="grid-filter" v-model="filterUsertype"
          :options="usertypeFilterOptions" :placeholder="$t('accountMgmt.roleAll')"
          @update:model-value="applyFilter"></id-select>
        <span class="grid-spacer"></span>
        <button class="id-btn id-btn-ghost id-btn-sm" @click="initialize">
          <span class="mdi mdi-refresh"></span> {{ $t('btn.refresh') }}</button>
      </div>

      <div style="overflow-x:auto">
        <table class="idt">
          <thead><tr>
            <th v-for="h in userHeaders" :key="h.key"
              :class="{ 'idt-action': h.key === 'edit' }"
              :style="{ textAlign: h.align === 'center' ? 'center' : 'left', cursor: h.sortable === false ? 'default' : 'pointer' }"
              @click="applySort(h)">
              {{ h.title }}
              <span v-if="$sortDir(tableOptions.sortBy, h.key)" class="mdi"
                :class="$sortDir(tableOptions.sortBy, h.key) === 'desc' ? 'mdi-arrow-down' : 'mdi-arrow-up'"></span>
            </th>
          </tr></thead>

          <tbody v-if="loadingUsers" class="id-fade-in" key="sk">
            <tr v-for="i in 6" :key="'sk' + i"><td v-for="h in userHeaders" :key="h.key">
              <div class="id-skel" style="height:18px"></div></td></tr>
          </tbody>

          <tbody v-else class="id-fade-in" :key="tableOptions.page + ':' + search + ':' + filterUsertype">
            <tr v-for="u in userList" :key="u.username" style="cursor:pointer" @click="clickEditUser(u)">
              <td>{{ u.username }}</td>
              <td>{{ [u.firstname, u.middlename, u.lastname].filter(Boolean).join(' ') }}</td>
              <td>{{ u.mobileno || '—' }}</td>
              <td style="text-align:center">{{ roleLabel(u.usertype) }}</td>
              <td>{{ u.province || '—' }}</td>
              <td style="text-align:center">
                <span :style="{ color: u.access_restricted ? 'var(--c-error)' : 'var(--c-success)', fontWeight: 600 }">
                  {{ u.access_restricted ? $t('accountMgmt.restricted') : $t('accountMgmt.active') }}</span>
              </td>
              <td class="idt-action" style="text-align:center">
                <span class="mdi mdi-history" style="color:var(--c-primary);font-size:20px;margin-right:8px"
                  @click.stop="showAccountHistory(u)"></span>
                <span class="mdi mdi-account-edit" style="color:var(--c-info);font-size:20px"
                  @click.stop="clickEditUser(u)"></span></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="!loadingUsers && userList.length === 0" class="grid-empty">
        <span class="mdi mdi-account-off-outline"></span> {{ $t('accountMgmt.noUsers') }}</div>

      <id-grid-footer v-if="totalUsers > 0"
        :page="tableOptions.page"
        :page-count="Math.max(1, Math.ceil(totalUsers / tableOptions.itemsPerPage))"
        :per-page="tableOptions.itemsPerPage" :total="totalUsers"
        @update:page="(p) => { tableOptions.page = p; getUserList(true); }"
        @update:per-page="(n) => { tableOptions.itemsPerPage = n; tableOptions.page = 1; getUserList(true); }" />
    </div>

    <!-- ══════════ editor modal (4 tabs) ══════════ -->
    <!-- steady-height: dialog นี้มี 4 แท็บที่ความสูงต่างกันมาก (ส่วนตัว ~600px vs รหัสผ่าน ~200px)
         ถ้าไม่ล็อก กล่องจะยืด-หดทุกครั้งที่สลับแท็บ -->
    <id-modal v-model="dialogUser" size="xl" persistent steady-height icon="mdi-account-cog"
      :title="$t('accountMgmt.editUser')"
      :subtitle="editedUser ? editedUser.username + ' · ' + roleLabel(editedUser.usertype) : ''">

      <div v-if="editorLocked" class="am-note am-note-warn">
        <span class="mdi mdi-shield-lock-outline"></span> {{ $t('accountMgmt.adminStaffBlocked') }}
      </div>

      <!-- tab toggle -->
      <div class="am-tabs">
        <button type="button" class="id-btn id-btn-sm"
          :class="activeTab === 'personal' ? 'id-btn-primary' : 'id-btn-ghost'" @click="activeTab = 'personal'">
          <span class="mdi mdi-account-outline"></span> {{ $t('accountMgmt.tabPersonal') }}</button>
        <button type="button" class="id-btn id-btn-sm"
          :class="activeTab === 'password' ? 'id-btn-primary' : 'id-btn-ghost'" @click="activeTab = 'password'">
          <span class="mdi mdi-lock-reset"></span> {{ $t('accountMgmt.tabPassword') }}</button>
        <button type="button" class="id-btn id-btn-sm"
          :class="activeTab === 'family' ? 'id-btn-primary' : 'id-btn-ghost'" @click="switchToFamily()">
          <span class="mdi mdi-account-group-outline"></span> {{ $t('accountMgmt.tabFamily') }}</button>
        <button v-if="isHeadUser" type="button" class="id-btn id-btn-sm"
          :class="activeTab === 'permissions' ? 'id-btn-primary' : 'id-btn-ghost'" @click="activeTab = 'permissions'">
          <span class="mdi mdi-shield-account-outline"></span> {{ $t('accountMgmt.tabPermissions') }}</button>
      </div>

      <!-- แท็บทั้ง 4 ใช้ v-show + .id-tabpane ตามมาตรฐาน steady-height ของ IdModal:
           ต้องอยู่ใน DOM ครบทุกแท็บ dialog ถึงจะวัดความสูงล่วงหน้าแล้วล็อกไว้ตั้งแต่เปิดได้
           (ห้ามเปลี่ยนกลับเป็น v-if และห้ามใส่ .id-fade-in ที่แพน — จะเล่นแค่รอบแรกรอบเดียว) -->
      <!-- ── Tab 1: personal ── -->
      <div v-show="activeTab === 'personal'" class="id-tabpane" key="tab-personal">
        <v-form ref="personalForm">
          <div class="modal-sec"><span class="mdi mdi-card-account-details-outline"></span>
            {{ $t('accountMgmt.personalSection') }}</div>
          <div class="form-grid-3">
            <div class="field"><label>{{ $t('accountMgmt.firstname') }} <span class="req">*</span></label>
              <input class="id-input" v-model="editForm.firstname" :disabled="editorLocked"></div>
            <div class="field"><label>{{ $t('accountMgmt.middlename') }}</label>
              <input class="id-input" v-model="editForm.middlename" :disabled="editorLocked"></div>
            <div class="field"><label>{{ $t('accountMgmt.lastname') }} <span class="req">*</span></label>
              <input class="id-input" v-model="editForm.lastname" :disabled="editorLocked"></div>
          </div>
          <div class="modal-sec mt"><span class="mdi mdi-phone-outline"></span> {{ $t('accountMgmt.contactSection') }}</div>
          <div class="form-grid">
            <div class="field"><label>{{ $t('accountMgmt.email') }}</label>
              <input class="id-input" type="email" v-model="editForm.email" :disabled="editorLocked"></div>
            <div class="field"><label>{{ $t('accountMgmt.mobileno') }}</label>
              <input class="id-input" v-model="editForm.mobileno" :disabled="editorLocked"></div>
          </div>
          <div class="modal-sec mt"><span class="mdi mdi-map-marker-outline"></span> {{ $t('accountMgmt.addressSection') }}</div>
          <div class="form-grid" :class="{ 'am-disabled': editorLocked }">
            <thai-address-fields
              v-model:address-no="editForm.address_no" v-model:subdistrict="editForm.subdistrict"
              v-model:district="editForm.district" v-model:province="editForm.province"
              v-model:postalcode="editForm.postalcode"></thai-address-fields>
          </div>
        </v-form>
      </div>

      <!-- ── Tab 2: password ── -->
      <div v-show="activeTab === 'password'" class="id-tabpane" key="tab-password">
        <div class="modal-sec"><span class="mdi mdi-lock-reset"></span> {{ $t('accountMgmt.passwordSection') }}</div>
        <div class="am-note">
          <span class="mdi mdi-information-outline"></span> {{ $t('accountMgmt.pwSessionNote') }}
        </div>
        <!-- ⚠️ autocomplete="new-password" ห้ามถอด: ไม่ใส่แล้วเบราว์เซอร์จะเดาว่าฟอร์มนี้คือหน้าล็อกอิน
             แล้วเติมรหัสที่จำไว้ให้ พร้อมยัด "ชื่อผู้ใช้" ลงช่อง text ที่ใกล้ที่สุด ซึ่งคือช่องค้นหาของ
             กริดด้านหลัง → กริดยิงค้นหาเองเงียบ ๆ (เจ้าของเจอ 2026-08-22: สลับมาแท็บนี้แล้วกริดหลัง
             dialog กลายเป็น filter "tnpl1") · ตั้งแต่เปลี่ยนแท็บเป็น v-show ช่องพวกนี้อยู่ใน DOM ตลอด
             แม้ยังไม่เข้าแท็บ จึง disable ไว้เมื่อไม่ได้อยู่แท็บนี้ — ช่องที่ disable ไม่ถูก autofill
             และความสูงเท่าเดิมทุกประการ การวัดความสูงล่วงหน้าจึงไม่เพี้ยน -->
        <div class="form-grid">
          <div class="field"><label>{{ $t('accountMgmt.newPassword') }} <span class="req">*</span></label>
            <div class="id-pass">
              <input class="id-input" :type="showPw1 ? 'text' : 'password'" v-model="pw.next"
                autocomplete="new-password" :disabled="editorLocked || activeTab !== 'password'">
              <button type="button" class="id-eye" @click="showPw1 = !showPw1">
                <span class="mdi" :class="showPw1 ? 'mdi-eye' : 'mdi-eye-off'"></span></button>
            </div></div>
          <div class="field"><label>{{ $t('accountMgmt.confirmPassword') }} <span class="req">*</span></label>
            <div class="id-pass">
              <input class="id-input" :type="showPw2 ? 'text' : 'password'" v-model="pw.confirm"
                autocomplete="new-password" :disabled="editorLocked || activeTab !== 'password'">
              <button type="button" class="id-eye" @click="showPw2 = !showPw2">
                <span class="mdi" :class="showPw2 ? 'mdi-eye' : 'mdi-eye-off'"></span></button>
            </div></div>
        </div>
        <div v-if="pw.next && pw.confirm && pw.next !== pw.confirm"
          style="color:var(--c-error);font-size:12.5px;margin-top:6px">{{ $t('accountMgmt.pwMismatch') }}</div>
      </div>

      <!-- ── Tab 3: family ── -->
      <div v-show="activeTab === 'family'" class="id-tabpane" key="tab-family">
        <div class="modal-sec"><span class="mdi mdi-account-group-outline"></span> {{ $t('accountMgmt.familySection') }}</div>
        <div style="overflow-x:auto">
          <table class="idt">
            <thead><tr>
              <th>{{ $t('accountMgmt.childName') }}</th>
              <th>{{ $t('accountMgmt.childCourse') }}</th>
              <th style="text-align:center">{{ $t('accountMgmt.transferTo') }}</th>
              <th class="idt-action"></th>
            </tr></thead>
            <!-- ปกติแท็บนี้ถูก prefetch ตั้งแต่เปิด dialog แล้ว บล็อกนี้จึงแทบไม่ได้โผล่ —
                 ถ้าเน็ตช้าจริงก็กันที่เงียบ ๆ ไม่ใช้ .id-skel (shimmer ใน dialog = แถบเทาวาบ) -->
            <tbody v-if="loadingFamily" key="fsk">
              <tr><td colspan="4" class="am-fam-loading">
                <span v-if="loadingFamilySlow">{{ $t('common.loading') }}</span></td></tr>
            </tbody>
            <tbody v-else class="id-fade-in" :key="familyMembers.length">
              <tr v-for="m in familyMembers" :key="m.studentid">
                <td>{{ m.fullname || (m.firstname + ' ' + m.lastname) }}
                  <span v-if="m.journal === '1'" style="color:var(--c-warning);font-size:11px">({{ $t('accountMgmt.pending') }})</span>
                </td>
                <td>{{ m.coursename || '—' }}</td>
                <td style="min-width:220px">
                  <id-select :model-value="transferTarget[m.studentid] || ''" searchable
                    :search-placeholder="$t('accountMgmt.searchFamily')" :placeholder="$t('accountMgmt.chooseFamily')"
                    :options="familyLookup.filter(f => String(f.familyid) !== String(editedUser.familyid))
                      .map(f => ({ value: f.familyid, label: f.username + ' (#' + f.familyid + ')' }))"
                    @update:model-value="id => setTransferTarget(m.studentid, id)"></id-select>
                </td>
                <td class="idt-action" style="text-align:center;white-space:nowrap">
                  <button class="id-btn id-btn-ghost id-btn-sm"
                    :disabled="editorLocked || !transferTarget[m.studentid]" @click="doTransfer(m)">
                    <span class="mdi mdi-swap-horizontal"></span></button>
                  <span class="mdi mdi-delete-outline" style="color:var(--c-error);cursor:pointer;font-size:20px;margin-left:8px"
                    @click="confirmDeleteChild(m)"></span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="!loadingFamily && familyMembers.length === 0" class="grid-empty">
          <span class="mdi mdi-account-off-outline"></span> {{ $t('accountMgmt.noChildren') }}</div>

        <!-- add member -->
        <div class="modal-sec mt"><span class="mdi mdi-account-plus-outline"></span> {{ $t('accountMgmt.addChild') }}</div>
        <div class="form-grid-3">
          <div class="field"><label>{{ $t('accountMgmt.firstname') }} <span class="req">*</span></label>
            <input class="id-input" v-model="newChild.firstname" :disabled="editorLocked"></div>
          <div class="field"><label>{{ $t('accountMgmt.lastname') }} <span class="req">*</span></label>
            <input class="id-input" v-model="newChild.lastname" :disabled="editorLocked"></div>
          <div class="field"><label>{{ $t('accountMgmt.nickname') }} <span class="req">*</span></label>
            <input class="id-input" v-model="newChild.nickname" :disabled="editorLocked"></div>
          <div class="field"><label>{{ $t('accountMgmt.gender') }} <span class="req">*</span></label>
            <id-select v-model="newChild.gender" :placeholder="$t('common.select')"
              :options="[{ value: 'ชาย', label: $t('common.male') }, { value: 'หญิง', label: $t('common.female') }]"></id-select></div>
          <div class="field"><label>{{ $t('accountMgmt.dob') }} <span class="req">*</span></label>
            <id-date v-model="newChild.dateofbirth" :placeholder="$t('common.selectDob')"></id-date></div>
          <div class="field" style="align-self:end">
            <button class="id-btn id-btn-primary id-btn-sm"
              :disabled="editorLocked || !newChild.firstname || !newChild.lastname || !newChild.nickname || !newChild.gender || !newChild.dateofbirth"
              @click="doAddChild()"><span class="mdi mdi-content-save"></span> {{ $t('accountMgmt.addChild') }}</button>
          </div>
        </div>
      </div>

      <!-- ── Tab 4: permissions (head only) ── -->
      <div v-if="isHeadUser" v-show="activeTab === 'permissions'" class="id-tabpane" key="tab-permissions">
        <div class="modal-sec"><span class="mdi mdi-shield-account-outline"></span> {{ $t('accountMgmt.roleSection') }}</div>
        <div class="form-grid">
          <div class="field"><label>{{ $t('accountMgmt.roleLabel') }}</label>
            <id-select v-model="roleDraft" :disabled="editingSelf"
              :options="[
                { value: '0', label: $t('role.head') },
                { value: '1', label: $t('role.admin') },
                { value: '2', label: $t('role.coach') },
                { value: '10', label: $t('role.customer') }]"></id-select></div>
          <div class="field" style="align-self:end">
            <button class="id-btn id-btn-primary"
              :disabled="savingRole || editingSelf || roleDraft === String(editedUser.usertype)" @click="doSetRole">
              <span class="mdi mdi-content-save"></span> {{ $t('accountMgmt.applyRole') }}</button></div>
        </div>
        <div v-if="editingSelf" style="color:var(--c-warning);font-size:12.5px;margin-top:6px">
          {{ $t('accountMgmt.cannotChangeSelf') }}</div>

        <div class="modal-sec mt"><span class="mdi mdi-lock-open-variant-outline"></span> {{ $t('accountMgmt.accessSection') }}</div>
        <div class="field full" style="display:flex;align-items:center;gap:12px">
          <span :style="{ color: editedUser && editedUser.access_restricted ? 'var(--c-error)' : 'var(--c-success)', fontWeight: 600 }">
            {{ editedUser && editedUser.access_restricted ? $t('accountMgmt.restricted') : $t('accountMgmt.active') }}</span>
          <button v-if="editedUser && editedUser.access_restricted" class="id-btn id-btn-primary id-btn-sm"
            :disabled="savingAccess" @click="doUnlockAccess">
            <span class="mdi mdi-lock-open-variant"></span> {{ $t('accountMgmt.unlockAccess') }}</button>
          <span v-else style="color:var(--c-text-muted);font-size:12.5px">{{ $t('accountMgmt.notRestricted') }}</span>
        </div>
      </div>

      <template #footer>
        <button class="id-btn id-btn-ghost" @click="dialogUser = false">{{ $t('btn.close') }}</button>
        <button v-if="activeTab === 'personal'" class="id-btn id-btn-primary"
          :disabled="editorLocked || !editForm.firstname || !editForm.lastname" @click="savePersonal">
          <span class="mdi mdi-content-save"></span> {{ $t('btn.save') }}</button>
        <button v-else-if="activeTab === 'password'" class="id-btn id-btn-primary"
          :disabled="editorLocked || !pw.next || pw.next !== pw.confirm" @click="doResetPassword">
          <span class="mdi mdi-lock-reset"></span> {{ $t('accountMgmt.resetPasswordBtn') }}</button>
      </template>
    </id-modal>

    <!-- delete-member confirm -->
    <id-modal v-model="dialogDeleteChild" size="sm" icon="mdi-delete-alert-outline" :title="$t('accountMgmt.deleteChildTitle')">
      <p style="margin:0">{{ $t('accountMgmt.confirmDeleteChild') }}
        <b v-if="childToDelete">{{ childToDelete.fullname || childToDelete.firstname }}</b></p>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="dialogDeleteChild = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" style="background:var(--c-error)" @click="doDeleteChild">
          <span class="mdi mdi-delete"></span> {{ $t('btn.delete') }}</button>
      </template>
    </id-modal>

    <!-- ประวัติการแก้ไขบัญชี (read-only) — ไทม์ไลน์ taction_log ของ username นี้ -->
    <id-modal v-model="dialogAccountHistory" size="sm" icon="mdi-history"
      :title="$t('editHistory.title')" :subtitle="accountHistoryTitle">
      <edit-history-timeline v-if="dialogAccountHistory" kind="account"
        :refid="accountHistoryRefid" :preloaded="accountHistoryData"></edit-history-timeline>
      <template #footer>
        <button class="id-btn id-btn-primary" @click="dialogAccountHistory = false">{{ $t('btn.close') }}</button>
      </template>
    </id-modal>

    <!-- เตือน "อาจเป็นเด็กคนเดียวกัน" — จอนี้ก็สร้างเด็กได้ จึงเจอด่านเดียวกับหน้ารายชื่อนักเรียน
         ⚠️ ต้องอยู่หลัง id-modal ตัวอื่นทุกตัว ไม่งั้นถูก dialog แก้ไขบัญชีที่เปิดค้างบัง -->
    <duplicate-student-dialog v-model="dialogDup" :candidates="dupCandidates"
        :target-family-id="dupTargetFamilyId" :busy="dupBusy"
        @move="moveIntoFamily" @create-anyway="createChildAnyway" />
  </div>
</template>

<script>
import axios from 'axios';
import CryptoJS from 'crypto-js';
import { fetchEditHistory } from '@/utils/history';

// รายชื่อลูกในครอบครัวโหลดเสร็จเร็วกว่านี้เกือบทุกครั้ง (แถมถูก prefetch ตั้งแต่เปิด dialog)
// โชว์ตัวบอกสถานะทันทีจะกลายเป็นแถบวาบตอนสลับแท็บ — กติกาเดียวกับ EditHistoryTimeline
const FAMILY_SLOW_MS = 400;

export default {
  name: 'AccountManagement',
  props: {
    managerFlag: { type: Boolean, default: false },
  },
  data() {
    return {
      // grid
      userList: [],
      // dialog "ประวัติการแก้ไขบัญชี" (read-only) — ไทม์ไลน์ taction_log ของ username นี้
      dialogAccountHistory: false,
      accountHistoryRefid: null,
      accountHistoryTitle: '',
      accountHistoryData: null,
      totalUsers: 0,
      tableOptions: { page: 1, itemsPerPage: 10, sortBy: [] },
      search: '',
      searchTimer: null,
      filterUsertype: '',
      loadingUsers: false,
      // lookups
      familyLookup: [],
      // editor
      dialogUser: false,
      activeTab: 'personal',
      editedUser: null,
      editForm: {
        username: '', firstname: '', middlename: '', lastname: '', email: '', mobileno: '',
        address_no: '', subdistrict: '', district: '', province: '', postalcode: '',
      },
      // password
      pw: { next: '', confirm: '' },
      showPw1: false, showPw2: false,
      // family — โหลดล่วงหน้าตั้งแต่เปิด dialog (ดู clickEditUser) แท็บนี้จึงพร้อมก่อนถูกกด
      familyMembers: [],
      familyLoaded: false,      // โหลดจบแล้วหรือยัง (ครอบครัวว่างจริงก็ถือว่าจบ ไม่ยิงซ้ำทุกครั้งที่สลับแท็บ)
      familyInFlight: null,     // promise ที่ค้างอยู่ — กันยิงซ้อนตอนกดแท็บระหว่าง prefetch ยังไม่เสร็จ
      loadingFamily: false,
      loadingFamilySlow: false, // ช้าเกิน SLOW_MS ค่อยขึ้นข้อความ (กันแถบวาบตอนสลับแท็บ)
      transferTarget: {},
      newChild: { firstname: '', lastname: '', nickname: '', gender: '', dateofbirth: null },
      // กันเด็กซ้ำ — backend ตีกลับพร้อม duplicateWarning แทนที่จะสร้างให้เลย
      dialogDup: false,
      dupCandidates: [],
      dupPendingChild: null,
      dupTargetFamilyId: null,
      dupBusy: false,
      dialogDeleteChild: false,
      childToDelete: null,
      // permissions
      roleDraft: '',
      savingRole: false,
      savingAccess: false,
    };
  },
  computed: {
    token() { return this.$store.getters.getToken; },
    selfUsername() {
      const ud = this.$store.getters.getUserdata;
      return ud && ud.username ? ud.username : '';
    },
    isHeadUser() { return this.managerFlag; },
    editingSelf() { return this.editedUser && this.editedUser.username === this.selfUsername; },
    targetIsStaff() { return this.editedUser && ['0', '1', '2'].includes(String(this.editedUser.usertype)); },
    editorLocked() { return !this.isHeadUser && this.targetIsStaff; },
    userHeaders() {
      return [
        { title: this.$t('accountMgmt.username'), key: 'username' },
        { title: this.$t('accountMgmt.fullname'), key: 'fullname', sortable: false },
        { title: this.$t('accountMgmt.mobileno'), key: 'mobileno', sortable: false },
        { title: this.$t('accountMgmt.roleCol'), key: 'usertype', align: 'center' },
        { title: this.$t('accountMgmt.province'), key: 'province', sortable: false },
        { title: this.$t('accountMgmt.status'), key: 'status', align: 'center', sortable: false },
        { title: '', key: 'edit', align: 'center', sortable: false },
      ];
    },
    usertypeFilterOptions() {
      return [
        { value: '', label: this.$t('accountMgmt.roleAll') },
        { value: '0', label: this.$t('role.head') },
        { value: '1', label: this.$t('role.admin') },
        { value: '2', label: this.$t('role.coach') },
        { value: '10', label: this.$t('role.customer') },
      ];
    },
  },
  watch: {
    search() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.tableOptions.page = 1; this.getUserList(); }, 500);
    },
  },
  async created() {
    if (!this.token) { this.$emit('onClickChangeState', 'login'); return; }
    try {
      await axios.post(this.baseURL + '/verifyToken', {}, { headers: { Authorization: `Bearer ${this.token}` } });
    } catch (e) {
      this.$emit('onClickChangeState', 'login'); return;
    }
    this.initialize();
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer);
    clearTimeout(this.slowFamilyTimer);
  },
  methods: {
    roleLabel(code) {
      return {
        '0': this.$t('role.head'), '1': this.$t('role.admin'),
        '2': this.$t('role.coach'), '10': this.$t('role.customer'),
      }[String(code)] || String(code);
    },
    async initialize() {
      this.tableOptions.page = 1;
      this.$emit('onLoading', true);
      await this.getFamilyLookup();
      await this.getUserList();
      this.$emit('onLoading', false);
    },
    async getFamilyLookup() {
      try {
        const res = await axios.get(this.baseURL + '/familyLookup', { headers: { Authorization: `Bearer ${this.token}` } });
        this.familyLookup = res.data.success ? (res.data.results || []) : [];
      } catch (e) { this.familyLookup = []; }
    },
    async getUserList(animate = false) {
      const t0 = Date.now();
      this.loadingUsers = true;
      try {
        const res = await axios.get(this.baseURL + '/getUserList', {
          headers: { Authorization: `Bearer ${this.token}` },
          params: {
            page: this.tableOptions.page,
            itemsPerPage: this.tableOptions.itemsPerPage,
            search: this.search,
            usertype: this.filterUsertype,
            sortBy: JSON.stringify(this.tableOptions.sortBy || []),
          },
        });
        if (res.data.success) {
          this.userList = res.data.results || [];
          this.totalUsers = res.data.total || 0;
        }
      } catch (e) {
        if (e.response && e.response.status === 401) { this.$emit('onClickChangeState', 'login'); return; }
        this.$emit('onErrorHandler', this.$t('accountMgmt.loadFail'));
      } finally {
        if (animate) await this.$minLoad(t0);
        this.loadingUsers = false;
      }
    },
    applySort(h) {
      if (h.sortable === false) return;
      this.tableOptions.sortBy = this.$toggleSort(this.tableOptions.sortBy, h.key);
      this.tableOptions.page = 1;
      this.getUserList(true);
    },
    applyFilter() { this.tableOptions.page = 1; this.getUserList(); },
    // ปุ่มประวัติการแก้ไขบัญชี — preload ไทม์ไลน์ให้ครบก่อนเปิด (เหมือน showStudentHistory)
    async showAccountHistory(u) {
      this.accountHistoryRefid = u.username;
      this.accountHistoryTitle = [u.firstname, u.lastname].filter(Boolean).join(' ') || u.username;
      this.$emit('onLoading', true);
      try {
        this.accountHistoryData = await fetchEditHistory(
          this.baseURL, this.$store.getters.getToken, 'account', u.username);
      } finally {
        this.$emit('onLoading', false);
      }
      this.dialogAccountHistory = true;
    },
    clickEditUser(u) {
      this.editedUser = { ...u };
      this.editForm = {
        username: u.username, firstname: u.firstname, middlename: u.middlename || '',
        lastname: u.lastname, email: u.email || '', mobileno: u.mobileno || '',
        address_no: u.address_no || '', subdistrict: u.subdistrict || '',
        district: u.district || '', province: u.province || '', postalcode: u.postalcode || '',
      };
      this.pw = { next: '', confirm: '' };
      this.showPw1 = false; this.showPw2 = false;
      this.roleDraft = String(u.usertype);
      this.familyMembers = [];
      this.familyLoaded = false;
      this.familyInFlight = null;
      this.transferTarget = {};
      this.newChild = { firstname: '', lastname: '', nickname: '', gender: '', dateofbirth: null };
      this.activeTab = 'personal';
      this.dialogUser = true;
      // prefetch แท็บครอบครัวเงียบ ๆ — dialog เปิดทันทีเหมือนเดิม (แท็บแรกเป็นข้อมูลในเครื่องล้วน)
      // แต่พอผู้ใช้กดแท็บครอบครัวข้อมูลมาถึงแล้ว = ไม่มีจังหวะโหลดคั่นกลาง
      // ยิงเฉพาะบัญชีที่มีครอบครัวจริง (สตาฟไม่มี familyid) จะได้ไม่ยิงคำขอเปล่า
      if (u.familyid) this.loadFamilyMembers();
    },
    // ── Tab 1 ──
    async savePersonal() {
      this.$emit('onLoading', true);
      try {
        const res = await axios.post(this.baseURL + '/adminUpdateUser', {
          username: this.editedUser.username,
          firstname: this.editForm.firstname,
          middlename: this.editForm.middlename,
          lastname: this.editForm.lastname,
          email: this.editForm.email,
          mobileno: this.editForm.mobileno,
          address_no: this.editForm.address_no,
          subdistrict: this.editForm.subdistrict,
          district: this.editForm.district,
          province: this.editForm.province,
          postalcode: this.editForm.postalcode,
        }, { headers: { Authorization: `Bearer ${this.token}` } });
        if (res.data.success) {
          this.$emit('onInfoHandler', this.$t('accountMgmt.savedProfile'));
          this.getUserList();
        } else {
          this.$emit('onErrorHandler', res.data.message || this.$t('accountMgmt.saveFail'));
        }
      } catch (e) {
        this.$emit('onErrorHandler', this.$t('accountMgmt.saveFail'));
      } finally { this.$emit('onLoading', false); }
    },
    // ── Tab 2 ──
    async doResetPassword() {
      this.$emit('onLoading', true);
      try {
        const hashed = CryptoJS.SHA256(this.pw.next).toString();
        const res = await axios.post(this.baseURL + '/adminResetPassword',
          { username: this.editedUser.username, newPassword: hashed },
          { headers: { Authorization: `Bearer ${this.token}` } });
        if (res.data.success) {
          this.$emit('onInfoHandler', this.$t('accountMgmt.pwResetOk'));
          this.pw = { next: '', confirm: '' };
        } else {
          this.$emit('onErrorHandler', res.data.message || this.$t('accountMgmt.saveFail'));
        }
      } catch (e) {
        this.$emit('onErrorHandler', this.$t('accountMgmt.saveFail'));
      } finally { this.$emit('onLoading', false); }
    },
    // ── Tab 3 ──
    // แท็บต้องสลับทันทีเสมอ (คนกดแท็บคาดหวังว่ามันเปลี่ยนเดี๋ยวนั้น) — ความสมูทมาจาก prefetch
    // ตอนเปิด dialog ไม่ใช่จากการหน่วงตรงนี้ · ถ้า prefetch ยังไม่จบก็แค่รอ promise เดิม ไม่ยิงซ้ำ
    switchToFamily() {
      this.activeTab = 'family';
      if (!this.familyLoaded && !this.familyInFlight) this.loadFamilyMembers();
    },
    // เรียก 2 ทาง: prefetch ตอนเปิด dialog (ไม่ await) · รีเฟรชหลังย้าย/ลบ/เพิ่มเด็ก (force + await)
    // คืน promise เสมอ เพื่อให้ผู้เรียกที่อยากรอจริง ๆ รอได้
    loadFamilyMembers({ force = false } = {}) {
      if (!force && this.familyInFlight) return this.familyInFlight;   // กดแท็บระหว่าง prefetch = รอใบเดิม
      const familyid = this.editedUser && this.editedUser.familyid;
      // บัญชีสตาฟไม่มีครอบครัว — ไม่ต้องยิงคำขอที่ยังไงก็ได้ลิสต์ว่าง (เดิมยิงทุกครั้งที่กดแท็บ)
      if (!familyid) { this.familyMembers = []; this.familyLoaded = true; return Promise.resolve(); }
      const seq = (this.familySeq = (this.familySeq || 0) + 1);
      this.familyInFlight = (async () => {
        this.loadingFamily = true;
        clearTimeout(this.slowFamilyTimer);
        this.slowFamilyTimer = setTimeout(() => { this.loadingFamilySlow = true; }, FAMILY_SLOW_MS);
        try {
          const res = await axios.post(this.baseURL + '/getFamilyList',
            { familyid }, { headers: { Authorization: `Bearer ${this.token}` } });
          // ทิ้งผลที่มาช้า: ผู้ใช้อาจปิดแล้วเปิดบัญชีอื่นระหว่างที่ prefetch ยังค้าง หรือมีคำขอใหม่กว่า
          // แซงไปแล้ว — ไม่งั้นลูกของบัญชีก่อนหน้าจะไปโผล่ในแท็บครอบครัวของอีกคน
          if (seq !== this.familySeq || !this.editedUser || this.editedUser.familyid !== familyid) return;
          this.familyMembers = res.data.success ? (res.data.results || []) : [];
          this.familyLoaded = true;
        } catch (e) {
          if (seq === this.familySeq && this.editedUser && this.editedUser.familyid === familyid) {
            this.familyMembers = [];
          }
        } finally {
          if (seq === this.familySeq) {
            clearTimeout(this.slowFamilyTimer);
            this.loadingFamily = false;
            this.loadingFamilySlow = false;
            this.familyInFlight = null;
          }
        }
      })();
      return this.familyInFlight;
    },
    setTransferTarget(studentid, id) { this.transferTarget = { ...this.transferTarget, [studentid]: id }; },
    async doTransfer(m) {
      const target = this.transferTarget[m.studentid];
      if (!target) return;
      this.$emit('onLoading', true);
      try {
        const res = await axios.post(this.baseURL + '/transferStudent',
          { studentid: m.studentid, familyid: target },
          { headers: { Authorization: `Bearer ${this.token}` } });
        if (res.data.success) {
          this.$emit('onInfoHandler', this.$t('accountMgmt.transferOk'));
          this.setTransferTarget(m.studentid, '');
          await this.loadFamilyMembers({ force: true });   // รอให้ตารางอัปเดตก่อนค่อยปิดม่านโหลด
        } else {
          this.$emit('onErrorHandler', res.data.message || this.$t('accountMgmt.saveFail'));
        }
      } catch (e) {
        this.$emit('onErrorHandler', this.$t('accountMgmt.saveFail'));
      } finally { this.$emit('onLoading', false); }
    },
    confirmDeleteChild(m) { this.childToDelete = m; this.dialogDeleteChild = true; },
    async doDeleteChild() {
      const m = this.childToDelete;
      this.dialogDeleteChild = false;
      if (!m) return;
      this.$emit('onLoading', true);
      try {
        const res = await axios.post(this.baseURL + '/deleteStudent',
          { familyid: this.editedUser.familyid, studentid: m.studentid, journal: m.journal || '0' },
          { headers: { Authorization: `Bearer ${this.token}` } });
        if (res.data.success) {
          this.$emit('onInfoHandler', this.$t('accountMgmt.childDeleted'));
          await this.loadFamilyMembers({ force: true });
        } else {
          this.$emit('onErrorHandler', res.data.message || this.$t('accountMgmt.saveFail'));
        }
      } catch (e) {
        this.$emit('onErrorHandler', this.$t('accountMgmt.saveFail'));
      } finally { this.$emit('onLoading', false); }
    },
    async doAddChild(confirmDuplicate, reason) {
      this.$emit('onLoading', true);
      try {
        const c = this.newChild;
        const body = {
          firstname: c.firstname, middlename: '', lastname: c.lastname, nickname: c.nickname,
          gender: c.gender, dateofbirth: c.dateofbirth, familyid: this.editedUser.familyid,
        };
        if (confirmDuplicate) { body.confirmDuplicate = true; body.reason = reason; }
        const res = await axios.post(this.baseURL + '/addStudentByAdmin', body,
          { headers: { Authorization: `Bearer ${this.token}` } });
        if (res.data.success) {
          this.$emit('onInfoHandler', this.$t('accountMgmt.childAdded'));
          this.newChild = { firstname: '', lastname: '', nickname: '', gender: '', dateofbirth: null };
          this.closeDup();
          await this.loadFamilyMembers({ force: true });
        } else if (res.data.duplicateWarning) {
          // ⚠️ ห้ามตกลงไปที่ else — ข้อความจะกลายเป็น error dialog แล้วกดบันทึกซ้ำก็ได้ผลเดิม
          // ตลอดไป จอนี้ไม่มีทางอื่นให้เพิ่มเด็ก = ฝาแฝด/เด็กชื่อพ้องกันจะถูกบล็อกถาวร
          this.dupCandidates = res.data.candidates || [];
          this.dupPendingChild = { ...c };
          this.dupTargetFamilyId = this.editedUser.familyid;
          this.dialogDup = true;
        } else {
          this.$emit('onErrorHandler', res.data.message || this.$t('accountMgmt.saveFail'));
        }
      } catch (e) {
        this.$emit('onErrorHandler', this.$t('accountMgmt.saveFail'));
      } finally { this.$emit('onLoading', false); }
    },
    createChildAnyway(reason) { this.doAddChild(true, reason); },
    // ย้ายแถวเดิมเข้าครอบครัวที่กำลังเปิดอยู่ แทนการสร้างเด็กใหม่ (ประวัติ/คอร์สติดตามไปเอง)
    async moveIntoFamily(candidate) {
      if (!this.dupTargetFamilyId) return;
      this.dupBusy = true;
      this.$emit('onLoading', true);
      try {
        const res = await axios.post(this.baseURL + '/transferStudent',
          { studentid: candidate.studentid, familyid: this.dupTargetFamilyId },
          { headers: { Authorization: `Bearer ${this.token}` } });
        if (res.data.success) {
          this.$emit('onInfoHandler', this.$t('dup.moved'));
          this.newChild = { firstname: '', lastname: '', nickname: '', gender: '', dateofbirth: null };
          this.closeDup();
          await this.loadFamilyMembers({ force: true });
        } else {
          this.$emit('onErrorHandler', res.data.message);
        }
      } catch (e) {
        this.$emit('onErrorHandler', e.response?.data?.message || e.message);
      } finally {
        this.dupBusy = false;
        this.$emit('onLoading', false);
      }
    },
    closeDup() {
      this.dialogDup = false;
      this.dupCandidates = [];
      this.dupPendingChild = null;
      this.dupTargetFamilyId = null;
    },
    // ── Tab 4 ──
    async doSetRole() {
      this.savingRole = true;
      this.$emit('onLoading', true);
      try {
        const res = await axios.post(this.baseURL + '/adminSetUserRole',
          { username: this.editedUser.username, usertype: this.roleDraft },
          { headers: { Authorization: `Bearer ${this.token}` } });
        if (res.data.success) {
          this.$emit('onInfoHandler', res.data.message || this.$t('accountMgmt.roleChanged'));
          this.editedUser.usertype = this.roleDraft;
          this.getUserList();
        } else {
          this.$emit('onErrorHandler', res.data.message || this.$t('accountMgmt.saveFail'));
        }
      } catch (e) {
        this.$emit('onErrorHandler', this.$t('accountMgmt.saveFail'));
      } finally { this.savingRole = false; this.$emit('onLoading', false); }
    },
    async doUnlockAccess() {
      this.savingAccess = true;
      this.$emit('onLoading', true);
      try {
        const res = await axios.post(this.baseURL + '/adminSetUserAccess',
          { username: this.editedUser.username },
          { headers: { Authorization: `Bearer ${this.token}` } });
        if (res.data.success) {
          this.$emit('onInfoHandler', this.$t('accountMgmt.accessUnlocked'));
          this.editedUser.access_restricted = 0;
          this.getUserList();
        } else {
          this.$emit('onErrorHandler', res.data.message || this.$t('accountMgmt.saveFail'));
        }
      } catch (e) {
        this.$emit('onErrorHandler', this.$t('accountMgmt.saveFail'));
      } finally { this.savingAccess = false; this.$emit('onLoading', false); }
    },
  },
};
</script>

<style scoped>
.am-tabs {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  border-bottom: 1px solid var(--c-border, rgba(0, 0, 0, 0.08));
  padding-bottom: 10px;
}
.am-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: var(--radius-md);
  background: rgba(59, 130, 246, 0.10);
  border: 1px solid rgba(59, 130, 246, 0.30);
  font-size: 12.5px;
  color: var(--c-text-heading);
  margin-bottom: 12px;
}
.am-note .mdi { color: var(--c-info, #3b82f6); font-size: 18px; flex: 0 0 auto; }
/* กันที่ระหว่างโหลดรายชื่อลูก — สูงเท่าแถวจริงพอดี ตารางจึงไม่กระโดดตอนข้อมูลมา */
.am-fam-loading { height: 38px; color: var(--c-text-muted); font-size: 13px; }
.am-note-warn {
  background: rgba(255, 179, 0, 0.12);
  border-color: rgba(255, 179, 0, 0.35);
}
.am-note-warn .mdi { color: var(--c-warning, #e6a700); }
/* address picker read-only lock while an admin views a staff row */
.am-disabled { opacity: 0.6; pointer-events: none; }
</style>
